import type { BiomassDataRow, ProductionUnitTableContext } from '../../utils/productionUnitTable'

import { getOfflineDb } from '../dbFactory'

export type ProductionTableRowEntity = BiomassDataRow & {
  pk: string
  farmId: string
  productionUnitId: string
  updatedAt: string
}

const pkFor = (farmId: string, productionUnitId: string, rowId: string): string => (
  `${farmId}::${productionUnitId}::${rowId}`
)

const toEntity = (context: ProductionUnitTableContext, row: BiomassDataRow): ProductionTableRowEntity => ({
  ...row,
  pk: pkFor(context.farmId, context.productionUnitId, row.id),
  farmId: context.farmId,
  productionUnitId: context.productionUnitId,
  updatedAt: new Date().toISOString()
})

const toRow = (entity: ProductionTableRowEntity): BiomassDataRow => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { pk, farmId, productionUnitId, updatedAt, ...row } = entity
  return row
}

export class DexieProductionTableRepo {
  async listByContext(context: ProductionUnitTableContext): Promise<BiomassDataRow[]> {
    const db = getOfflineDb()
    const entities = await db.productionTableRows
      .where('[farmId+productionUnitId]')
      .equals([context.farmId, context.productionUnitId])
      .toArray() as ProductionTableRowEntity[]

    return entities
      .map(toRow)
      .sort((a, b) => String(b.date ?? '').localeCompare(String(a.date ?? '')))
  }

  async listAllEntities(): Promise<ProductionTableRowEntity[]> {
    const db = getOfflineDb()
    const entities = await db.productionTableRows.toArray() as ProductionTableRowEntity[]
    return entities.sort((a, b) => String(b.date ?? '').localeCompare(String(a.date ?? '')))
  }

  async countByContext(context: ProductionUnitTableContext): Promise<number> {
    const db = getOfflineDb()
    return db.productionTableRows
      .where('[farmId+productionUnitId]')
      .equals([context.farmId, context.productionUnitId])
      .count()
  }

  async upsertRow(context: ProductionUnitTableContext, row: BiomassDataRow): Promise<void> {
    const db = getOfflineDb()
    await db.productionTableRows.put(toEntity(context, row))
  }

  async upsertMany(context: ProductionUnitTableContext, rows: BiomassDataRow[]): Promise<void> {
    if (rows.length === 0) return
    const db = getOfflineDb()
    const entities = rows.map((row) => toEntity(context, row))
    await db.productionTableRows.bulkPut(entities)
  }

  async deleteRow(context: ProductionUnitTableContext, rowId: string): Promise<void> {
    const db = getOfflineDb()
    await db.productionTableRows.delete(pkFor(context.farmId, context.productionUnitId, rowId))
  }

  /** Used when a single lot is deleted (not its whole farm/PU) — those
   * paths already clear every row for the context via
   * `replaceAllForContext`, but a lot-level delete needs to remove just
   * this lot's rows from a PU shared with other, still-live lots. */
  async deleteAllForLotId(lotId: string): Promise<void> {
    const db = getOfflineDb()
    const keys = await db.productionTableRows.where('lotId').equals(lotId).primaryKeys()
    if (keys.length === 0) return
    await db.productionTableRows.bulkDelete(keys as string[])
  }

  async replaceAllForContext(context: ProductionUnitTableContext, rows: BiomassDataRow[]): Promise<void> {
    const db = getOfflineDb()

    await db.transaction('rw', db.productionTableRows, async () => {
      const existingKeys = await db.productionTableRows
        .where('[farmId+productionUnitId]')
        .equals([context.farmId, context.productionUnitId])
        .primaryKeys()

      if (existingKeys.length > 0) {
        await db.productionTableRows.bulkDelete(existingKeys as string[])
      }

      if (rows.length > 0) {
        await db.productionTableRows.bulkPut(rows.map((row) => toEntity(context, row)))
      }
    })
  }
}

export const dexieProductionTableRepo = new DexieProductionTableRepo()
